var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { useMemo, useRef, useSyncExternalStore, useCallback, useLayoutEffect, useEffect, useDebugValue } from "react";
import axios from "axios";
var AuthProvider = /* @__PURE__ */ ((AuthProvider2) => {
  AuthProvider2["Google"] = "google";
  AuthProvider2["Github"] = "github";
  AuthProvider2["Web3"] = "web3";
  return AuthProvider2;
})(AuthProvider || {});
const TRACK_MEMO_SYMBOL = Symbol();
const GET_ORIGINAL_SYMBOL = Symbol();
const AFFECTED_PROPERTY = "a";
const IS_TARGET_COPIED_PROPERTY = "f";
const PROXY_PROPERTY = "p";
const PROXY_CACHE_PROPERTY = "c";
const TARGET_CACHE_PROPERTY = "t";
const HAS_KEY_PROPERTY = "h";
const ALL_OWN_KEYS_PROPERTY = "w";
const HAS_OWN_KEY_PROPERTY = "o";
const KEYS_PROPERTY = "k";
let newProxy$1 = (target, handler) => new Proxy(target, handler);
const getProto = Object.getPrototypeOf;
const objectsToTrack = /* @__PURE__ */ new WeakMap();
const isObjectToTrack = (obj) => obj && (objectsToTrack.has(obj) ? objectsToTrack.get(obj) : getProto(obj) === Object.prototype || getProto(obj) === Array.prototype);
const isObject$1 = (x) => typeof x === "object" && x !== null;
const needsToCopyTargetObject = (obj) => Object.values(Object.getOwnPropertyDescriptors(obj)).some((descriptor) => !descriptor.configurable && !descriptor.writable);
const copyTargetObject = (obj) => {
  if (Array.isArray(obj)) {
    return Array.from(obj);
  }
  const descriptors = Object.getOwnPropertyDescriptors(obj);
  Object.values(descriptors).forEach((desc) => {
    desc.configurable = true;
  });
  return Object.create(getProto(obj), descriptors);
};
const createProxyHandler = (origObj, isTargetCopied) => {
  const state = {
    [IS_TARGET_COPIED_PROPERTY]: isTargetCopied
  };
  let trackObject = false;
  const recordUsage = (type, key) => {
    if (!trackObject) {
      let used = state[AFFECTED_PROPERTY].get(origObj);
      if (!used) {
        used = {};
        state[AFFECTED_PROPERTY].set(origObj, used);
      }
      if (type === ALL_OWN_KEYS_PROPERTY) {
        used[ALL_OWN_KEYS_PROPERTY] = true;
      } else {
        let set = used[type];
        if (!set) {
          set = /* @__PURE__ */ new Set();
          used[type] = set;
        }
        set.add(key);
      }
    }
  };
  const recordObjectAsUsed = () => {
    trackObject = true;
    state[AFFECTED_PROPERTY].delete(origObj);
  };
  const handler = {
    get(target, key) {
      if (key === GET_ORIGINAL_SYMBOL) {
        return origObj;
      }
      recordUsage(KEYS_PROPERTY, key);
      return createProxy(Reflect.get(target, key), state[AFFECTED_PROPERTY], state[PROXY_CACHE_PROPERTY], state[TARGET_CACHE_PROPERTY]);
    },
    has(target, key) {
      if (key === TRACK_MEMO_SYMBOL) {
        recordObjectAsUsed();
        return true;
      }
      recordUsage(HAS_KEY_PROPERTY, key);
      return Reflect.has(target, key);
    },
    getOwnPropertyDescriptor(target, key) {
      recordUsage(HAS_OWN_KEY_PROPERTY, key);
      return Reflect.getOwnPropertyDescriptor(target, key);
    },
    ownKeys(target) {
      recordUsage(ALL_OWN_KEYS_PROPERTY);
      return Reflect.ownKeys(target);
    }
  };
  if (isTargetCopied) {
    handler.set = handler.deleteProperty = () => false;
  }
  return [handler, state];
};
const getOriginalObject = (obj) => (
  // unwrap proxy
  obj[GET_ORIGINAL_SYMBOL] || // otherwise
  obj
);
const createProxy = (obj, affected, proxyCache2, targetCache2) => {
  if (!isObjectToTrack(obj))
    return obj;
  let targetAndCopied = targetCache2 && targetCache2.get(obj);
  if (!targetAndCopied) {
    const target2 = getOriginalObject(obj);
    if (needsToCopyTargetObject(target2)) {
      targetAndCopied = [target2, copyTargetObject(target2)];
    } else {
      targetAndCopied = [target2];
    }
    targetCache2 === null || targetCache2 === void 0 ? void 0 : targetCache2.set(obj, targetAndCopied);
  }
  const [target, copiedTarget] = targetAndCopied;
  let handlerAndState = proxyCache2 && proxyCache2.get(target);
  if (!handlerAndState || handlerAndState[1][IS_TARGET_COPIED_PROPERTY] !== !!copiedTarget) {
    handlerAndState = createProxyHandler(target, !!copiedTarget);
    handlerAndState[1][PROXY_PROPERTY] = newProxy$1(copiedTarget || target, handlerAndState[0]);
    if (proxyCache2) {
      proxyCache2.set(target, handlerAndState);
    }
  }
  handlerAndState[1][AFFECTED_PROPERTY] = affected;
  handlerAndState[1][PROXY_CACHE_PROPERTY] = proxyCache2;
  handlerAndState[1][TARGET_CACHE_PROPERTY] = targetCache2;
  return handlerAndState[1][PROXY_PROPERTY];
};
const isAllOwnKeysChanged = (prevObj, nextObj) => {
  const prevKeys = Reflect.ownKeys(prevObj);
  const nextKeys = Reflect.ownKeys(nextObj);
  return prevKeys.length !== nextKeys.length || prevKeys.some((k, i) => k !== nextKeys[i]);
};
const isChanged = (prevObj, nextObj, affected, cache, isEqual = Object.is) => {
  if (isEqual(prevObj, nextObj)) {
    return false;
  }
  if (!isObject$1(prevObj) || !isObject$1(nextObj))
    return true;
  const used = affected.get(getOriginalObject(prevObj));
  if (!used)
    return true;
  if (cache) {
    const hit = cache.get(prevObj);
    if (hit === nextObj) {
      return false;
    }
    cache.set(prevObj, nextObj);
  }
  let changed = null;
  for (const key of used[HAS_KEY_PROPERTY] || []) {
    changed = Reflect.has(prevObj, key) !== Reflect.has(nextObj, key);
    if (changed)
      return changed;
  }
  if (used[ALL_OWN_KEYS_PROPERTY] === true) {
    changed = isAllOwnKeysChanged(prevObj, nextObj);
    if (changed)
      return changed;
  } else {
    for (const key of used[HAS_OWN_KEY_PROPERTY] || []) {
      const hasPrev = !!Reflect.getOwnPropertyDescriptor(prevObj, key);
      const hasNext = !!Reflect.getOwnPropertyDescriptor(nextObj, key);
      changed = hasPrev !== hasNext;
      if (changed)
        return changed;
    }
  }
  for (const key of used[KEYS_PROPERTY] || []) {
    changed = isChanged(prevObj[key], nextObj[key], affected, cache, isEqual);
    if (changed)
      return changed;
  }
  if (changed === null)
    throw new Error("invalid used");
  return changed;
};
const getUntracked = (obj) => {
  if (isObjectToTrack(obj)) {
    return obj[GET_ORIGINAL_SYMBOL] || null;
  }
  return null;
};
const markToTrack = (obj, mark = true) => {
  objectsToTrack.set(obj, mark);
};
const affectedToPathList = (obj, affected, onlyWithValues) => {
  const list = [];
  const seen = /* @__PURE__ */ new WeakSet();
  const walk = (x, path) => {
    var _a, _b, _c;
    if (seen.has(x)) {
      return;
    }
    if (isObject$1(x)) {
      seen.add(x);
    }
    const used = isObject$1(x) && affected.get(getOriginalObject(x));
    if (used) {
      (_a = used[HAS_KEY_PROPERTY]) === null || _a === void 0 ? void 0 : _a.forEach((key) => {
        const segment = `:has(${String(key)})`;
        list.push(path ? [...path, segment] : [segment]);
      });
      if (used[ALL_OWN_KEYS_PROPERTY] === true) {
        const segment = ":ownKeys";
        list.push(path ? [...path, segment] : [segment]);
      } else {
        (_b = used[HAS_OWN_KEY_PROPERTY]) === null || _b === void 0 ? void 0 : _b.forEach((key) => {
          const segment = `:hasOwn(${String(key)})`;
          list.push(path ? [...path, segment] : [segment]);
        });
      }
      (_c = used[KEYS_PROPERTY]) === null || _c === void 0 ? void 0 : _c.forEach((key) => {
        if (!onlyWithValues || "value" in (Object.getOwnPropertyDescriptor(x, key) || {})) {
          walk(x[key], path ? [...path, key] : [key]);
        }
      });
    } else if (path) {
      list.push(path);
    }
  };
  walk(obj);
  return list;
};
var define_import_meta_env_default$1 = { BASE_URL: "/", MODE: "production", DEV: false, PROD: true, SSR: false };
const isObject = (x) => typeof x === "object" && x !== null;
const canProxyDefault = (x) => isObject(x) && !refSet.has(x) && (Array.isArray(x) || !(Symbol.iterator in x)) && !(x instanceof WeakMap) && !(x instanceof WeakSet) && !(x instanceof Error) && !(x instanceof Number) && !(x instanceof Date) && !(x instanceof String) && !(x instanceof RegExp) && !(x instanceof ArrayBuffer) && !(x instanceof Promise);
const createSnapshotDefault = (target, version) => {
  const cache = snapCache.get(target);
  if ((cache == null ? void 0 : cache[0]) === version) {
    return cache[1];
  }
  const snap = Array.isArray(target) ? [] : Object.create(Object.getPrototypeOf(target));
  markToTrack(snap, true);
  snapCache.set(target, [version, snap]);
  Reflect.ownKeys(target).forEach((key) => {
    if (Object.getOwnPropertyDescriptor(snap, key)) {
      return;
    }
    const value = Reflect.get(target, key);
    const { enumerable } = Reflect.getOwnPropertyDescriptor(
      target,
      key
    );
    const desc = {
      value,
      enumerable,
      // This is intentional to avoid copying with proxy-compare.
      // It's still non-writable, so it avoids assigning a value.
      configurable: true
    };
    if (refSet.has(value)) {
      markToTrack(value, false);
    } else if (proxyStateMap.has(value)) {
      const [target2, ensureVersion] = proxyStateMap.get(
        value
      );
      desc.value = createSnapshotDefault(target2, ensureVersion());
    }
    Object.defineProperty(snap, key, desc);
  });
  return Object.preventExtensions(snap);
};
const createHandlerDefault = (isInitializing, addPropListener, removePropListener, notifyUpdate) => ({
  deleteProperty(target, prop) {
    const prevValue = Reflect.get(target, prop);
    removePropListener(prop);
    const deleted = Reflect.deleteProperty(target, prop);
    if (deleted) {
      notifyUpdate(["delete", [prop], prevValue]);
    }
    return deleted;
  },
  set(target, prop, value, receiver) {
    const hasPrevValue = !isInitializing() && Reflect.has(target, prop);
    const prevValue = Reflect.get(target, prop, receiver);
    if (hasPrevValue && (objectIs(prevValue, value) || proxyCache.has(value) && objectIs(prevValue, proxyCache.get(value)))) {
      return true;
    }
    removePropListener(prop);
    if (isObject(value)) {
      value = getUntracked(value) || value;
    }
    const nextValue = !proxyStateMap.has(value) && canProxy(value) ? proxy(value) : value;
    addPropListener(prop, nextValue);
    Reflect.set(target, prop, nextValue, receiver);
    notifyUpdate(["set", [prop], value, prevValue]);
    return true;
  }
});
const proxyStateMap = /* @__PURE__ */ new WeakMap();
const refSet = /* @__PURE__ */ new WeakSet();
const snapCache = /* @__PURE__ */ new WeakMap();
const versionHolder = [1, 1];
const proxyCache = /* @__PURE__ */ new WeakMap();
let objectIs = Object.is;
let newProxy = (target, handler) => new Proxy(target, handler);
let canProxy = canProxyDefault;
let createSnapshot = createSnapshotDefault;
let createHandler = createHandlerDefault;
function proxy(baseObject = {}) {
  if (!isObject(baseObject)) {
    throw new Error("object required");
  }
  const found = proxyCache.get(baseObject);
  if (found) {
    return found;
  }
  let version = versionHolder[0];
  const listeners = /* @__PURE__ */ new Set();
  const notifyUpdate = (op, nextVersion = ++versionHolder[0]) => {
    if (version !== nextVersion) {
      version = nextVersion;
      listeners.forEach((listener) => listener(op, nextVersion));
    }
  };
  let checkVersion = versionHolder[1];
  const ensureVersion = (nextCheckVersion = ++versionHolder[1]) => {
    if (checkVersion !== nextCheckVersion && !listeners.size) {
      checkVersion = nextCheckVersion;
      propProxyStates.forEach(([propProxyState]) => {
        const propVersion = propProxyState[1](nextCheckVersion);
        if (propVersion > version) {
          version = propVersion;
        }
      });
    }
    return version;
  };
  const createPropListener = (prop) => (op, nextVersion) => {
    const newOp = [...op];
    newOp[1] = [prop, ...newOp[1]];
    notifyUpdate(newOp, nextVersion);
  };
  const propProxyStates = /* @__PURE__ */ new Map();
  const addPropListener = (prop, propValue) => {
    const propProxyState = !refSet.has(propValue) && proxyStateMap.get(propValue);
    if (propProxyState) {
      if ((define_import_meta_env_default$1 ? "production" : void 0) !== "production" && propProxyStates.has(prop)) {
        throw new Error("prop listener already exists");
      }
      if (listeners.size) {
        const remove = propProxyState[2](createPropListener(prop));
        propProxyStates.set(prop, [propProxyState, remove]);
      } else {
        propProxyStates.set(prop, [propProxyState]);
      }
    }
  };
  const removePropListener = (prop) => {
    var _a;
    const entry = propProxyStates.get(prop);
    if (entry) {
      propProxyStates.delete(prop);
      (_a = entry[1]) == null ? void 0 : _a.call(entry);
    }
  };
  const addListener = (listener) => {
    listeners.add(listener);
    if (listeners.size === 1) {
      propProxyStates.forEach(([propProxyState, prevRemove], prop) => {
        if ((define_import_meta_env_default$1 ? "production" : void 0) !== "production" && prevRemove) {
          throw new Error("remove already exists");
        }
        const remove = propProxyState[2](createPropListener(prop));
        propProxyStates.set(prop, [propProxyState, remove]);
      });
    }
    const removeListener = () => {
      listeners.delete(listener);
      if (listeners.size === 0) {
        propProxyStates.forEach(([propProxyState, remove], prop) => {
          if (remove) {
            remove();
            propProxyStates.set(prop, [propProxyState]);
          }
        });
      }
    };
    return removeListener;
  };
  let initializing = true;
  const handler = createHandler(
    () => initializing,
    addPropListener,
    removePropListener,
    notifyUpdate
  );
  const proxyObject = newProxy(baseObject, handler);
  proxyCache.set(baseObject, proxyObject);
  const proxyState = [baseObject, ensureVersion, addListener];
  proxyStateMap.set(proxyObject, proxyState);
  Reflect.ownKeys(baseObject).forEach((key) => {
    const desc = Object.getOwnPropertyDescriptor(
      baseObject,
      key
    );
    if ("value" in desc && desc.writable) {
      proxyObject[key] = baseObject[key];
    }
  });
  initializing = false;
  return proxyObject;
}
function subscribe(proxyObject, callback, notifyInSync) {
  const proxyState = proxyStateMap.get(proxyObject);
  if ((define_import_meta_env_default$1 ? "production" : void 0) !== "production" && !proxyState) {
    console.warn("Please use proxy object");
  }
  let promise;
  const ops = [];
  const addListener = proxyState[2];
  let isListenerActive = false;
  const listener = (op) => {
    ops.push(op);
    if (notifyInSync) {
      callback(ops.splice(0));
      return;
    }
    if (!promise) {
      promise = Promise.resolve().then(() => {
        promise = void 0;
        if (isListenerActive) {
          callback(ops.splice(0));
        }
      });
    }
  };
  const removeListener = addListener(listener);
  isListenerActive = true;
  return () => {
    isListenerActive = false;
    removeListener();
  };
}
function snapshot(proxyObject) {
  const proxyState = proxyStateMap.get(proxyObject);
  if ((define_import_meta_env_default$1 ? "production" : void 0) !== "production" && !proxyState) {
    console.warn("Please use proxy object");
  }
  const [target, ensureVersion] = proxyState;
  return createSnapshot(target, ensureVersion());
}
var define_import_meta_env_default = { BASE_URL: "/", MODE: "production", DEV: false, PROD: true, SSR: false };
const useAffectedDebugValue = (state, affected) => {
  const pathList = useRef(void 0);
  useEffect(() => {
    pathList.current = affectedToPathList(state, affected, true);
  });
  useDebugValue(pathList.current);
};
const condUseAffectedDebugValue = useAffectedDebugValue;
const targetCache = /* @__PURE__ */ new WeakMap();
function useSnapshot(proxyObject, options) {
  const notifyInSync = options == null ? void 0 : options.sync;
  const affected = useMemo(
    () => proxyObject && /* @__PURE__ */ new WeakMap(),
    [proxyObject]
  );
  const lastSnapshot = useRef(void 0);
  let inRender = true;
  const currSnapshot = useSyncExternalStore(
    useCallback(
      (callback) => {
        const unsub = subscribe(proxyObject, callback, notifyInSync);
        callback();
        return unsub;
      },
      [proxyObject, notifyInSync]
    ),
    () => {
      const nextSnapshot = snapshot(proxyObject);
      try {
        if (!inRender && lastSnapshot.current && !isChanged(
          lastSnapshot.current,
          nextSnapshot,
          affected,
          /* @__PURE__ */ new WeakMap()
        )) {
          return lastSnapshot.current;
        }
      } catch (e) {
      }
      return nextSnapshot;
    },
    () => snapshot(proxyObject)
  );
  inRender = false;
  useLayoutEffect(() => {
    lastSnapshot.current = currSnapshot;
  });
  if ((define_import_meta_env_default ? "production" : void 0) !== "production") {
    condUseAffectedDebugValue(currSnapshot, affected);
  }
  const proxyCache2 = useMemo(() => /* @__PURE__ */ new WeakMap(), []);
  return createProxy(currSnapshot, affected, proxyCache2, targetCache);
}
const ignoreKeys = ["isInitialized"];
const initialStore = {
  isSignedIn: false,
  provider: void 0,
  expiresAt: void 0,
  accessToken: void 0,
  referralCode: void 0,
  isMarketingAccepted: void 0,
  authAddress: void 0,
  walletId: void 0,
  changeCounter: 0,
  isInitialized: false
};
const authQueryParamKeys = [
  "provider",
  "authApiBase",
  "authCode",
  "authAddress",
  "isMarketingAccepted",
  "referralCode",
  "walletId"
];
function getUrlParams(search) {
  const urlParams = new URLSearchParams(search);
  return authQueryParamKeys.reduce((acc, key) => {
    return { ...acc, [key]: urlParams.get(key) || void 0 };
  }, {});
}
function persistStore(store2, key, excludeKeys = []) {
  const saved = localStorage.getItem(key);
  if (saved) {
    const { authCode } = getUrlParams(window.location.search);
    if (!authCode) {
      try {
        Object.assign(store2, { ...initialStore, ...JSON.parse(saved) });
      } catch {
        console.warn("Failed to parse persisted auth state");
      }
    }
  }
  return subscribe(store2, () => {
    const state = snapshot(store2);
    const filteredState = Object.fromEntries(
      Object.entries(state).filter(([key2]) => !excludeKeys.includes(key2))
    );
    localStorage.setItem(key, JSON.stringify(filteredState));
  });
}
const store = proxy({ ...initialStore });
persistStore(store, "auth-store", ignoreKeys);
function resetUrlParams() {
  const params = new URLSearchParams(window.location.search);
  Array.from(params.keys()).forEach((key) => {
    if (authQueryParamKeys.indexOf(key) !== -1) {
      params.delete(key);
    }
  });
  const paramsSting = params.toString();
  const newUrl = `${window.location.pathname}${paramsSting ? `?${paramsSting}` : ""}`;
  window.history.replaceState({}, "", newUrl);
}
async function tryToSignIn({
  appId,
  getPermanentAccessToken,
  gateways,
  signOutFromAPI,
  customAuthAPIBaseURL
}) {
  const {
    provider,
    authApiBase,
    authCode,
    authAddress,
    isMarketingAccepted,
    referralCode,
    walletId
  } = getUrlParams(window.location.search);
  const authBaseURL = customAuthAPIBaseURL ?? authApiBase;
  if (!authBaseURL) {
    throw new Error(`Missing authentication params`);
  }
  if (authCode) {
    try {
      const response = await getPermanentAccessToken({
        authCode,
        baseURL: authBaseURL,
        provider
      });
      store.isSignedIn = true;
      store.authAddress = authAddress;
      store.provider = provider;
      store.isMarketingAccepted = isMarketingAccepted;
      store.referralCode = referralCode;
      store.accessToken = response.accessToken;
      store.expiresAt = response.expiresAt;
      store.walletId = walletId;
      await gateways.auth.init({
        appId,
        authBaseURL,
        accessToken: response.accessToken,
        expiresAt: response.expiresAt,
        signOutFromAPI
      });
    } catch (error) {
      await gateways.auth.signOut();
    } finally {
      resetUrlParams();
    }
  } else if (store.accessToken && store.expiresAt) {
    await gateways.auth.init({
      appId,
      authBaseURL,
      accessToken: store.accessToken,
      expiresAt: store.expiresAt,
      signOutFromAPI
    });
  }
  if (!store.isInitialized) {
    store.isInitialized = true;
  }
}
let inited = false;
const useAuth = ({
  appId,
  getPermanentAccessToken,
  signOutFromAPI,
  gateways,
  customAuthAPIBaseURL
}) => {
  const { auth } = gateways;
  const { isSignedIn, isInitialized } = useSnapshot(store);
  useEffect(() => {
    if (inited) {
      return;
    }
    inited = true;
    void tryToSignIn({
      appId,
      getPermanentAccessToken,
      gateways,
      signOutFromAPI,
      customAuthAPIBaseURL
    });
  }, []);
  const handleSignOut = useCallback(async () => {
    await auth.signOut();
  }, [auth]);
  return {
    isSignedIn,
    isInitialized,
    handleSignOut
  };
};
function useAuthSubscribe(callback) {
  useEffect(() => {
    if (store.isSignedIn) {
      callback(snapshot(store));
    }
    return subscribe(store, () => {
      const data = snapshot(store);
      callback(data);
    });
  }, [callback]);
}
function assignToStore(target, source, ignoreKeys2) {
  Object.keys(source).filter((key) => {
    if (!ignoreKeys2) {
      return true;
    }
    return !ignoreKeys2.includes(key);
  }).forEach((key) => {
    const value = source[key];
    if (key in target) {
      target[key] = value;
    }
  });
}
function resetStore() {
  assignToStore(store, initialStore, ignoreKeys);
  store.changeCounter += 1;
}
class GatewayAuth {
  constructor(config) {
    __publicField(this, "api");
    __publicField(this, "accessToken");
    __publicField(this, "expiresAt");
    __publicField(this, "abortController", new AbortController());
    __publicField(this, "appId");
    __publicField(this, "authBaseURL");
    __publicField(this, "signOutFromAPI");
    this.config = config;
    this.api = axios.create({
      ...this.config,
      signal: this.abortController.signal
    });
    this.setAccessTokenInterceptor();
  }
  setAccessTokenInterceptor() {
    this.api.interceptors.request.use((config) => {
      if (this.accessToken) {
        config.headers.Authorization = `Bearer ${this.accessToken}`;
        return config;
      }
      return config;
    });
  }
  async init({
    appId,
    authBaseURL,
    accessToken,
    expiresAt,
    signOutFromAPI
  }) {
    this.authBaseURL = authBaseURL;
    this.signOutFromAPI = signOutFromAPI;
    this.appId = appId;
    this.expiresAt = expiresAt;
    if (this.isTokenExpired()) {
      await this.signOut();
    } else {
      this.signIn({
        expiresAt,
        accessToken
      });
    }
  }
  async signOut() {
    const token = this.accessToken;
    try {
      if (token && this.appId && this.authBaseURL && !this.isTokenExpired()) {
        if (this.signOutFromAPI) {
          await this.signOutFromAPI({
            application: this.appId,
            baseURL: this.authBaseURL,
            token
          });
        }
      }
    } finally {
      this.reset();
    }
  }
  reset() {
    this.abortController.abort();
    this.accessToken = void 0;
    this.expiresAt = void 0;
    this.appId = void 0;
    this.authBaseURL = void 0;
    this.signOutFromAPI = void 0;
    resetStore();
  }
  signIn({
    accessToken,
    expiresAt
  }) {
    this.accessToken = accessToken;
    this.expiresAt = expiresAt;
  }
  isTokenExpired() {
    return this.expiresAt && Date.now() > parseInt(this.expiresAt, 10);
  }
}
class GatewayNoAuth {
  constructor(config) {
    __publicField(this, "api");
    this.config = config;
    this.api = axios.create({
      ...this.config
    });
  }
}
const defaultConfig = {
  headers: {
    "Content-Type": "application/json"
  },
  responseType: "json"
};
function createGateways({
  baseURL,
  withCredentials = true
}) {
  return {
    auth: new GatewayAuth({
      ...defaultConfig,
      baseURL,
      withCredentials
    }),
    noAuth: new GatewayNoAuth({
      ...defaultConfig,
      baseURL,
      withCredentials
    })
  };
}
function getSignInUrl({
  authPath,
  appPath,
  appId,
  referralCode,
  hasReferralCodeBox,
  hasClickwrapAgreement,
  hasAPIKeysNotification,
  referrer
}) {
  const authUrl = new URL(authPath);
  authUrl.searchParams.set("appPath", appPath);
  authUrl.searchParams.set("appId", appId);
  authUrl.searchParams.set("referrer", referrer);
  if (referralCode) {
    authUrl.searchParams.set("referralCode", referralCode);
  }
  if (hasReferralCodeBox) {
    authUrl.searchParams.set("hasReferralCodeBox", "true");
  }
  if (hasClickwrapAgreement) {
    authUrl.searchParams.set("hasClickwrapAgreement", "true");
  }
  if (hasAPIKeysNotification) {
    authUrl.searchParams.set("hasAPIKeysNotification", "true");
  }
  return authUrl;
}
const getPermanentAccessTokenWithCookie = async ({ authCode, baseURL }) => {
  const { data } = await axios.post(
    "/auth/session/ui/new",
    { token: authCode },
    { baseURL }
  );
  return { accessToken: data.access_token, expiresAt: data.expires_at };
};
const signOutAndClearCookies = async ({
  baseURL,
  token
}) => {
  await axios.post("/auth/session/ui/logout", void 0, {
    baseURL,
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
};
const signOut = async ({
  application,
  baseURL,
  token
}) => {
  await axios.post("/killSessionByToken", { application, token }, { baseURL });
};
export {
  AuthProvider,
  createGateways,
  getPermanentAccessTokenWithCookie,
  getSignInUrl,
  signOut,
  signOutAndClearCookies,
  useAuth,
  useAuthSubscribe
};
